import React, { useState } from "react";
import Box from "@/ui/atoms/Box";
import { creationTestIds } from "./utils";

export type FormStepsProps = JSX.IntrinsicAttributes & {
    nextText?: string;
    currentStep: number;
    totalSteps: number;
    editingProperty: boolean;
    onBack: () => void;
    onNext: () => void | Promise<void>;
    isNextDisabled: boolean;
    useSubNavbarIds: boolean;
};

const FormSteps: React.FC<FormStepsProps> = ({
    nextText,
    currentStep,
    totalSteps,
    editingProperty,
    onBack,
    onNext,
    isNextDisabled,
    useSubNavbarIds,
}) => {
    const [clicked, setClicked] = useState(false);
    const isDisabled = clicked || isNextDisabled;
    const handleNext = () => {
        if (isDisabled) return;
        setClicked(true);
        try {
            const result = onNext();
            void Promise.resolve(result).finally(() => {
                setClicked(false);
            });
        } catch (error) {
            setClicked(false);
            throw error;
        }
    };

    // if currentStep is different from last render, unset clicked
    const [currentStepState, setCurrentStepState] = useState({
        currentStep,
        editingProperty,
    });
    React.useEffect(() => {
        if (
            currentStepState.currentStep !== currentStep ||
            currentStepState.editingProperty !== editingProperty
        ) {
            setClicked(false);
            setCurrentStepState({ currentStep, editingProperty });
        }
    }, [currentStepState, setCurrentStepState, currentStep, editingProperty]);

    const nextId = useSubNavbarIds
        ? creationTestIds.subNavbarNextButton
        : creationTestIds.navbarNextButton;
    const backId = useSubNavbarIds
        ? creationTestIds.subNavbarBackButton
        : creationTestIds.navbarBackButton;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingBottom: "1rem",
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onBack()}
                sx={{
                    padding: "0.5rem 2rem",
                    border: "1px solid var(--proda-white-150)",
                    borderRadius: "4px",
                    backgroundColor: "var(--proda-white)",
                    cursor: "pointer",
                }}
                data-testid={backId}
            >
                Back
            </Box>

            <Box sx={{ flex: 1 }} />
            <Box
                component="button"
                type={currentStep === totalSteps ? "submit" : "button"}
                onClick={handleNext}
                disabled={isDisabled}
                sx={{
                    padding: "0.5rem 2rem",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: isDisabled
                        ? "var(--disabled-bg)"
                        : "var(--primary-color)",
                    color: "var(--proda-white)",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                }}
                data-testid={nextId}
            >
                {nextText !== undefined
                    ? nextText
                    : currentStep !== totalSteps
                      ? "Next"
                      : "Finish"}
            </Box>
        </Box>
    );
};

export default FormSteps;
